import type { QueryMatch, SyntaxNode } from 'web-tree-sitter';
import type { SymbolTag } from '../types.js';

function parseCaptureRole(captureName: string): { role: 'def' | 'ref'; kind: string } | null {
  if (captureName === 'name') return null;
  const dot = captureName.indexOf('.');
  if (dot < 0) return null;
  const prefix = captureName.slice(0, dot);
  const kind = captureName.slice(dot + 1);
  if (prefix === 'definition') return { role: 'def', kind };
  if (prefix === 'reference') return { role: 'ref', kind };
  return null;
}

function tagDedupeKey(tag: SymbolTag): string {
  return `${tag.startLine}:${tag.startColumn}:${tag.endLine}:${tag.endColumn}:${tag.role}:${tag.kind}:${tag.name}`;
}

export function tagsFromMatches(matches: QueryMatch[]): { tags: SymbolTag[]; nameNodes: Map<string, SyntaxNode> } {
  const tags: SymbolTag[] = [];
  const nameNodes = new Map<string, SyntaxNode>();
  const seen = new Set<string>();
  for (const match of matches) {
    let name: string | null = null;
    let nameNode: QueryMatch['captures'][number]['node'] | null = null;
    const roleNodes: Array<{ role: 'def' | 'ref'; kind: string; node: QueryMatch['captures'][number]['node'] }> = [];
    for (const capture of match.captures) {
      if (capture.name === 'name') {
        name = capture.node.text;
        nameNode = capture.node;
        continue;
      }
      const parsed = parseCaptureRole(capture.name);
      if (parsed) roleNodes.push({ ...parsed, node: capture.node });
    }
    if (!name || !nameNode || roleNodes.length === 0) continue;
    for (const roleNode of roleNodes) {
      const tag: SymbolTag = {
        name,
        role: roleNode.role,
        kind: roleNode.kind,
        startLine: nameNode.startPosition.row,
        startColumn: nameNode.startPosition.column,
        endLine: nameNode.endPosition.row,
        endColumn: nameNode.endPosition.column,
      };
      const key = tagDedupeKey(tag);
      if (seen.has(key)) continue;
      seen.add(key);
      tags.push(tag);
      nameNodes.set(`${tag.startLine}:${tag.startColumn}:${tag.name}:${tag.role}:${tag.kind}`, nameNode);
    }
  }
  return { tags, nameNodes };
}
